/**
 * Delta mush — ADR-028 E3's reference shape (D2's "derive at bake").
 *
 * Linear blend skinning collapses a bent joint; Laplacian smoothing removes
 * the collapse but also removes the character. Delta mush is the pairing:
 * record each vertex's detail as an offset in a LOCAL frame on the smoothed
 * REST mesh, then at a posed frame smooth again, rebuild the frame, and put
 * the detail back. The smoothing kills the artifact, the offset restores the
 * anatomy, and what comes out is what the elbow should have looked like.
 *
 * The frame is the subtle part, and the requirement is narrower than it looks:
 * it does not have to be canonical, it has to be the SAME MATERIAL FRAME at
 * rest and at pose. So it is built from a deterministically chosen pair of
 * welded neighbours — chosen once, at rest, and stored — rather than from
 * face normals or a ring order that a deforming mesh could reorder underneath
 * us. Same neighbours, same construction, both configurations.
 *
 * Rotation-equivariant by construction, which is the property worth testing:
 * rigidly rotate the mesh and the mush must reproduce the rotated mesh
 * exactly, because every frame rotates with it and every offset is expressed
 * in that frame.
 *
 * Depends on: mesh-smoothing. Depended on by: the crease derivation.
 */
import { buildWeldedGraph, smoothWelded, smoothWeldedTaubin, type WeldedGraph } from './mesh-smoothing.js';

export class DeltaMushError extends Error {}

/** Below this the chosen neighbour pair spans no plane and names no frame. */
const DEGENERATE = 1e-9;

/**
 * Smallest sin(angle) between a node's two frame edges worth calling a frame —
 * about 0.006°. Well above rounding, far below any real triangle.
 */
const MIN_FRAME_SIN = 1e-4;

export interface DeltaMushRest {
  readonly graph: WeldedGraph;
  /** Per node: the two welded neighbours whose edges build the frame. */
  readonly framePair: Int32Array;
  /** Per vertex: its detail offset, expressed in its node's rest frame. */
  readonly offsets: Float32Array;
  readonly iterations: number;
  readonly lambda: number;
  /**
   * Taubin's inflating factor, or null for plain Laplacian smoothing. Recorded
   * on the rest so pose-time smoothing is the SAME filter — the frames were
   * chosen on the rest smoothed this way, and a different filter at pose would
   * measure detail against a different surface.
   */
  readonly mu: number | null;
  /** Per node, a vertex that carries its position — nodes have no array slot of their own. */
  readonly nodeRep: Int32Array;
}

/** t, b, n as nine numbers — a rotation from frame space to mesh space. */
function frameAt(smoothed: Float32Array, nodeRep: Int32Array, node: number, a: number, b: number, out: Float64Array): void {
  const o = nodeRep[node]! * 3;
  const pa = nodeRep[a]! * 3;
  const pb = nodeRep[b]! * 3;
  let tx = smoothed[pa]! - smoothed[o]!, ty = smoothed[pa + 1]! - smoothed[o + 1]!, tz = smoothed[pa + 2]! - smoothed[o + 2]!;
  const ex = smoothed[pb]! - smoothed[o]!, ey = smoothed[pb + 1]! - smoothed[o + 1]!, ez = smoothed[pb + 2]! - smoothed[o + 2]!;
  const tl = Math.hypot(tx, ty, tz);
  if (tl < DEGENERATE) throw new DeltaMushError(`node ${node}: its frame edge collapsed`);
  tx /= tl; ty /= tl; tz /= tl;
  let nx = ty * ez - tz * ey, ny = tz * ex - tx * ez, nz = tx * ey - ty * ex;
  const nl = Math.hypot(nx, ny, nz);
  if (nl < DEGENERATE) throw new DeltaMushError(`node ${node}: its frame edges are collinear and span no plane`);
  nx /= nl; ny /= nl; nz /= nl;
  const bx = ny * tz - nz * ty, by = nz * tx - nx * tz, bz = nx * ty - ny * tx;
  out[0] = tx; out[1] = ty; out[2] = tz;
  out[3] = bx; out[4] = by; out[5] = bz;
  out[6] = nx; out[7] = ny; out[8] = nz;
}

/**
 * Capture the rest detail. Do this ONCE per character, on the bind mesh.
 */
/**
 * Smoothing defaults, MEASURED rather than guessed (2026-08-26, on a 120-degree
 * synthetic elbow, recovery of the joint girth LBS eats):
 *
 *     iters x lambda:  7.2 -> 0.39   18 -> 0.78   27 -> 0.88   36 -> 0.90   72 -> 0.89
 *
 * What matters is the PRODUCT — diffusion time — not either number alone: 60x0.6
 * and 120x0.3 both land on 0.902. It plateaus near 0.90 and then declines as
 * Laplacian shrinkage begins to eat the shape, so more is NOT better.
 *
 * The trap at the other end is worse than slow: UNDER-smoothing makes the
 * reference WORSE THAN LBS. At 4x0.3 recovery measured -0.069 — the "corrected"
 * elbow is thinner than the collapsed one, which would ship a morph that
 * deepens the very artifact it was derived to cancel.
 *
 * CAVEAT, unresolved: Laplacian diffusion scales with edge length, so this
 * product is RESOLUTION-DEPENDENT. These numbers come from one synthetic tube;
 * a dense Tripo body will want a different one, and the crease probe on a real
 * specimen is what should set it. Normalising by mean edge length is the
 * obvious next step and is not done here.
 */
export const DEFAULT_SMOOTH_ITERATIONS = 60;
export const DEFAULT_SMOOTH_LAMBDA = 0.6;

export function captureRestDetail(
  positions: Float32Array,
  index: ArrayLike<number> | null,
  iterations = DEFAULT_SMOOTH_ITERATIONS,
  lambda = DEFAULT_SMOOTH_LAMBDA,
  graph: WeldedGraph = buildWeldedGraph(positions, index),
  mu: number | null = null,
): DeltaMushRest {
  const vertexCount = positions.length / 3;
  const nodeRep = new Int32Array(graph.nodeCount).fill(-1);
  for (let v = 0; v < vertexCount; v += 1) {
    const n = graph.weldOf[v]!;
    if (nodeRep[n] === -1) nodeRep[n] = v;
  }
  const smoothed = mu === null ? smoothWelded(graph, positions, iterations, lambda) : smoothWeldedTaubin(graph, positions, iterations, lambda, mu);

  // Choose each node's frame pair ONCE, here, on the smoothed rest mesh: the
  // first neighbour, then the first further neighbour that spans a plane with
  // it. Neighbours are sorted, so the choice is deterministic and survives
  // any re-run — which is what makes rest and pose the same material frame.
  const framePair = new Int32Array(graph.nodeCount * 2).fill(-1);
  const frame = new Float64Array(9);
  const offsets = new Float32Array(positions.length);
  for (let n = 0; n < graph.nodeCount; n += 1) {
    const ring = graph.neighbors[n]!;
    if (ring.length < 2) continue; // no frame: this node keeps its smoothed position
    // The MOST orthogonal pair, not the first pair that merely works. A
    // barely-valid pair at rest can go collinear once the mesh deforms, and
    // then the frame it defines is noise exactly where the bend is sharpest.
    // Measured on the kobold: first-that-works produced nodes whose frame
    // collapsed at 120°. Neighbours are sorted, so ties break deterministically
    // and rest and pose still agree.
    const o = nodeRep[n]! * 3;
    let bestScore = 0;
    for (let i = 0; i < ring.length; i += 1) {
      const pa = nodeRep[ring[i]!]! * 3;
      let ax = smoothed[pa]! - smoothed[o]!, ay = smoothed[pa + 1]! - smoothed[o + 1]!, az = smoothed[pa + 2]! - smoothed[o + 2]!;
      const al = Math.hypot(ax, ay, az);
      if (al < DEGENERATE) continue;
      ax /= al; ay /= al; az /= al;
      for (let k = i + 1; k < ring.length; k += 1) {
        const pb = nodeRep[ring[k]!]! * 3;
        let bx = smoothed[pb]! - smoothed[o]!, by = smoothed[pb + 1]! - smoothed[o + 1]!, bz = smoothed[pb + 2]! - smoothed[o + 2]!;
        const bl = Math.hypot(bx, by, bz);
        if (bl < DEGENERATE) continue;
        bx /= bl; by /= bl; bz /= bl;
        // |cross| of two unit edges is sin(angle): the pair's actual
        // orthogonality, not merely "non-zero". A pair clearing zero by 1e-10
        // defines a frame made of rounding error — which is how the kobold
        // reached the offsets loop and threw (measured, 2026-08-26).
        const score = Math.hypot(ay * bz - az * by, az * bx - ax * bz, ax * by - ay * bx);
        if (score > bestScore && score > MIN_FRAME_SIN) {
          bestScore = score;
          framePair[n * 2] = ring[i]!;
          framePair[n * 2 + 1] = ring[k]!;
        }
      }
    }
  }

  for (let v = 0; v < vertexCount; v += 1) {
    const n = graph.weldOf[v]!;
    if (framePair[n * 2] === -1) continue; // frameless node: offset stays zero
    try {
      frameAt(smoothed, nodeRep, n, framePair[n * 2]!, framePair[n * 2 + 1]!, frame);
    } catch {
      // Belt and braces with the selection threshold: a node that still cannot
      // raise a frame keeps a zero offset and its smoothed position, rather
      // than costing the whole character its import.
      framePair[n * 2] = -1;
      framePair[n * 2 + 1] = -1;
      continue;
    }
    const dx = positions[v * 3]! - smoothed[v * 3]!;
    const dy = positions[v * 3 + 1]! - smoothed[v * 3 + 1]!;
    const dz = positions[v * 3 + 2]! - smoothed[v * 3 + 2]!;
    // Frame-space coordinates: the frame is orthonormal, so its inverse is its
    // transpose and this is three dot products.
    offsets[v * 3] = dx * frame[0]! + dy * frame[1]! + dz * frame[2]!;
    offsets[v * 3 + 1] = dx * frame[3]! + dy * frame[4]! + dz * frame[5]!;
    offsets[v * 3 + 2] = dx * frame[6]! + dy * frame[7]! + dz * frame[8]!;
  }
  return { graph, framePair, offsets, iterations, lambda, mu, nodeRep };
}

/**
 * The reference shape at a posed configuration: smooth the posed mesh, rebuild
 * each frame, and put the rest detail back into it.
 */
export interface MushResult {
  readonly positions: Float32Array;
  /**
   * Nodes whose frame collapsed AT POSE despite standing at rest, and which
   * therefore kept their smoothed position with no detail re-added.
   *
   * Counted rather than thrown. Refusing the whole mesh over one degenerate
   * vertex loses a character to a defect in a handful of triangles; silently
   * dropping them loses surface detail nobody can account for. So: the mesh
   * survives, slightly smoother in those spots, and the number says how many.
   */
  readonly framesLost: number;
}

export function mushPosed(rest: DeltaMushRest, posed: Float32Array): MushResult {
  if (posed.length !== rest.offsets.length) {
    throw new DeltaMushError(`posed mesh has ${posed.length / 3} vertices, the captured rest has ${rest.offsets.length / 3}`);
  }
  const smoothed = rest.mu === null ? smoothWelded(rest.graph, posed, rest.iterations, rest.lambda) : smoothWeldedTaubin(rest.graph, posed, rest.iterations, rest.lambda, rest.mu);
  const out = new Float32Array(posed.length);
  const frame = new Float64Array(9);
  const vertexCount = posed.length / 3;
  let framesLost = 0;
  for (let v = 0; v < vertexCount; v += 1) {
    const n = rest.graph.weldOf[v]!;
    const a = rest.framePair[n * 2]!;
    let usable = a !== -1;
    if (usable) {
      try {
        frameAt(smoothed, rest.nodeRep, n, a, rest.framePair[n * 2 + 1]!, frame);
      } catch {
        // The pair stood at rest and collapsed at pose — deep folds do this.
        usable = false;
        framesLost += 1;
      }
    }
    if (!usable) {
      out[v * 3] = smoothed[v * 3]!;
      out[v * 3 + 1] = smoothed[v * 3 + 1]!;
      out[v * 3 + 2] = smoothed[v * 3 + 2]!;
      continue;
    }
    const ox = rest.offsets[v * 3]!, oy = rest.offsets[v * 3 + 1]!, oz = rest.offsets[v * 3 + 2]!;
    out[v * 3] = smoothed[v * 3]! + ox * frame[0]! + oy * frame[3]! + oz * frame[6]!;
    out[v * 3 + 1] = smoothed[v * 3 + 1]! + ox * frame[1]! + oy * frame[4]! + oz * frame[7]!;
    out[v * 3 + 2] = smoothed[v * 3 + 2]! + ox * frame[2]! + oy * frame[5]! + oz * frame[8]!;
  }
  return { positions: out, framesLost };
}
